import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const defaultRoutes: Record<string, string> = {
  operator: '/',
  account_manager: '/leads',
  partner_principal: '/partner',
  partner_staff: '/partner',
  read_only: '/dashboard',
};

export default function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user) {
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
      return;
    }

    if (location.pathname === '/login') {
      navigate(defaultRoutes[effectiveRole] || '/dashboard', { replace: true });
      return;
    }

    const publicPaths = ['/login', '/offerte/', '/oplevering/'];
    if (publicPaths.some(p => location.pathname.startsWith(p))) {
      return;
    }

    const operatorAndReadOnly = ['/', '/dashboard', '/reports'];
    const operatorAndAM = ['/', '/leads', '/partners'];

    const isOperator = effectiveRole === 'operator';
    const isReadOnly = effectiveRole === 'read_only';
    const isAM = effectiveRole === 'account_manager';
    const isPartner = effectiveRole === 'partner_principal' || effectiveRole === 'partner_staff';

    if (isReadOnly && !operatorAndReadOnly.some(p => location.pathname.startsWith(p))) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (isAM && !operatorAndAM.some(p => location.pathname.startsWith(p))) {
      navigate('/leads', { replace: true });
      return;
    }

    if (isPartner && !location.pathname.startsWith('/partner')) {
      navigate('/partner', { replace: true });
      return;
    }

    if (!isOperator && !isReadOnly && !isAM && !isPartner && !['/dashboard', '/reports'].some(p => location.pathname.startsWith(p))) {
      navigate(defaultRoutes[effectiveRole] || '/dashboard', { replace: true });
      return;
    }
  }, [user, location.pathname, navigate]);

  if (!user && location.pathname !== '/login') {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: 'var(--bg-surface)' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[var(--text-muted)] border-t-[var(--accent)] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Checking session...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}